import { db, firebaseApp, functions } from '@/firebase/config'
import { waitForAppCheckToken } from '@/firebase/appCheck'
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  serverTimestamp
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'

const MAINTENANCE_DOC_PATH = 'system/maintenance'
const PERMISSION_RETRY_MS = 400

const MAINTENANCE_OFF = {
  isEnabled: false,
  message: null,
  lastUpdated: null
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const mapSnapshotToStatus = (snapshot) => {
  if (!snapshot.exists()) {
    return { ...MAINTENANCE_OFF, fetchSucceeded: true }
  }

  const data = snapshot.data()
  return {
    isEnabled: data.isEnabled || false,
    message: data.message || null,
    lastUpdated: data.lastUpdated || null,
    fetchSucceeded: true
  }
}

const readMaintenanceDoc = async () => {
  await waitForAppCheckToken(firebaseApp)
  const maintenanceRef = doc(db, MAINTENANCE_DOC_PATH)
  return getDoc(maintenanceRef)
}

export const checkMaintenanceStatus = async () => {
  try {
    const snapshot = await readMaintenanceDoc()
    return mapSnapshotToStatus(snapshot)
  } catch (error) {
    if (error?.code === 'permission-denied') {
      try {
        await delay(PERMISSION_RETRY_MS)
        const snapshot = await readMaintenanceDoc()
        return mapSnapshotToStatus(snapshot)
      } catch (retryError) {
        if (import.meta.env.DEV) {
          console.warn(
            '[Maintenance] Unable to read status after retry:',
            retryError
          )
        }
        return { ...MAINTENANCE_OFF, fetchSucceeded: false }
      }
    }

    if (import.meta.env.DEV) {
      console.warn('[Maintenance] Error checking status:', error)
    }
    return { ...MAINTENANCE_OFF, fetchSucceeded: false }
  }
}

export const subscribeToMaintenanceStatus = (callback) => {
  let unsubscribe = () => {}
  let cancelled = false

  const start = async () => {
    await waitForAppCheckToken(firebaseApp)
    if (cancelled) return

    const maintenanceRef = doc(db, MAINTENANCE_DOC_PATH)
    unsubscribe = onSnapshot(
      maintenanceRef,
      (snapshot) => {
        callback(mapSnapshotToStatus(snapshot))
      },
      (error) => {
        if (import.meta.env.DEV) {
          console.warn('[Maintenance] Subscription error:', error)
        }
        callback({ ...MAINTENANCE_OFF })
      }
    )
  }

  start()

  return () => {
    cancelled = true
    unsubscribe()
  }
}

export const verifyBypassPassword = async (passwordHash) => {
  try {
    if (
      !passwordHash ||
      typeof passwordHash !== 'string' ||
      passwordHash.trim() === ''
    ) {
      console.error(
        'Invalid passwordHash provided to verifyBypassPassword:',
        passwordHash
      )
      throw new Error('Password hash is required')
    }

    const verifyBypass = httpsCallable(functions, 'verifyMaintenanceBypass')
    const requestData = { passwordHash: passwordHash.trim() }

    const result = await verifyBypass(requestData)

    return result.data
  } catch (error) {
    console.error('Error verifying bypass password:', error)
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      details: error.details
    })
    throw error
  }
}

export const toggleMaintenanceMode = async (enabled, message, userId) => {
  try {
    const maintenanceRef = doc(db, MAINTENANCE_DOC_PATH)
    await setDoc(
      maintenanceRef,
      {
        isEnabled: enabled,
        message: message || null,
        lastUpdated: serverTimestamp(),
        updatedBy: userId
      },
      { merge: true }
    )
    return { success: true }
  } catch (error) {
    console.error('Error toggling maintenance mode:', error)
    throw error
  }
}

export const updateMaintenanceMessage = async (message, userId) => {
  try {
    const maintenanceRef = doc(db, MAINTENANCE_DOC_PATH)
    await setDoc(
      maintenanceRef,
      {
        message: message || null,
        lastUpdated: serverTimestamp(),
        updatedBy: userId
      },
      { merge: true }
    )
    return { success: true }
  } catch (error) {
    console.error('Error updating maintenance message:', error)
    throw error
  }
}

export const updateBypassPassword = async (newPasswordHash, userId) => {
  const passwordHash =
    typeof newPasswordHash === 'string' ? newPasswordHash.trim() : ''

  if (!/^[a-f0-9]{64}$/i.test(passwordHash)) {
    throw new Error('A valid password hash is required')
  }

  try {
    const maintenanceRef = doc(db, MAINTENANCE_DOC_PATH)
    await setDoc(
      maintenanceRef,
      {
        bypassPasswordHash: passwordHash,
        lastUpdated: serverTimestamp(),
        updatedBy: userId || null
      },
      { merge: true }
    )
    return { success: true }
  } catch (error) {
    console.error('Error updating bypass password:', error)
    throw error
  }
}
